import { useEffect, useState } from "react";
import { AdminGate } from "@/components/admin/AdminGate";
import { AdminLayout } from "@/components/admin/AdminLayout";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";

type ManualScraper = {
  id: string;
  email: string;
  name: string | null;
  status: "active" | "paused" | "terminated";
  lastActiveAt: string | null;
  totalAssignments: number;
  byStatus: Record<string, number>;
};

function AdminManualScrapersInner() {
  const [manualScrapers, setManualScrapers] = useState<ManualScraper[]>([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [form, setForm] = useState({ email: "", name: "" });
  const [toast, setToast] = useState<string | null>(null);
  const [resending, setResending] = useState<string | null>(null);

  async function refresh() {
    const res = await api<{ manualScrapers: ManualScraper[] }>("/admin/manual-scrapers");
    if (res.ok && res.data) {
      setManualScrapers(res.data.manualScrapers);
    }
    setLoading(false);
  }

  useEffect(() => {
    void refresh();
  }, []);

  async function createManualScraper(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setToast(null);
    setCreating(true);
    const res = await api<{ manualScraper: { id: string; email: string }; inviteSent: boolean }>(
      "/admin/manual-scrapers",
      {
        method: "POST",
        body: JSON.stringify({
          email: form.email.trim(),
          name: form.name.trim() || undefined,
        }),
      }
    );
    setCreating(false);
    if (!res.ok) {
      setError(res.error ?? "Could not create manual scraper.");
      return;
    }
    setToast(
      res.data?.inviteSent
        ? `Sent magic-link invite to ${res.data.manualScraper.email} (expires in 24h)`
        : `Created — but invite email failed to send. Use "Resend invite".`
    );
    setForm({ email: "", name: "" });
    void refresh();
  }

  async function setStatus(id: string, status: ManualScraper["status"]) {
    await api(`/admin/manual-scrapers/${id}`, {
      method: "PATCH",
      body: JSON.stringify({ status }),
    });
    void refresh();
  }

  async function resendInvite(s: ManualScraper) {
    setError(null);
    setToast(null);
    setResending(s.id);
    const res = await api(`/admin/manual-scrapers/${s.id}/resend-invite`, {
      method: "POST",
    });
    setResending(null);
    if (!res.ok) {
      setError(res.error ?? "Resend failed.");
      return;
    }
    setToast(`Sent a fresh magic link to ${s.email} (expires in 24h)`);
  }

  return (
    <AdminLayout>
      <h1 className="text-xl font-semibold tracking-tight">Manual scrapers</h1>
      <p className="mt-1 text-sm text-slate-600">
        Workers that manually transcribe Amazon reviews. They log in at{" "}
        <code>/manual-scrape/login</code> with their email below.
      </p>

      <section className="mt-6 rounded-2xl border border-slate-200 bg-white p-5">
        <h2 className="text-sm font-semibold">Add a manual scraper</h2>
        <form onSubmit={createManualScraper} className="mt-3 flex flex-wrap gap-3">
          <input
            type="email"
            required
            placeholder="worker@example.com"
            value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
            className="flex-1 min-w-[220px] rounded-lg border border-slate-300 px-3 py-2 text-sm"
          />
          <input
            type="text"
            placeholder="Display name (optional)"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            className="flex-1 min-w-[180px] rounded-lg border border-slate-300 px-3 py-2 text-sm"
          />
          <Button type="submit" disabled={creating} size="sm">
            {creating ? "Creating…" : "Add manual scraper"}
          </Button>
        </form>
        {error && (
          <p className="mt-2 text-xs text-red-600" role="alert">
            {error}
          </p>
        )}
        {toast && (
          <p className="mt-2 text-xs text-emerald-700" role="status">
            {toast}
          </p>
        )}
      </section>

      <section className="mt-6 overflow-hidden rounded-2xl border border-slate-200 bg-white">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-xs uppercase text-slate-500">
            <tr>
              <th className="px-4 py-2 text-left">Email</th>
              <th className="px-4 py-2 text-left">Name</th>
              <th className="px-4 py-2 text-left">Status</th>
              <th className="px-4 py-2 text-left">Last active</th>
              <th className="px-4 py-2 text-right">Pending</th>
              <th className="px-4 py-2 text-right">Claimed</th>
              <th className="px-4 py-2 text-right">Submitted</th>
              <th className="px-4 py-2 text-right">QA passed</th>
              <th className="px-4 py-2"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {loading && (
              <tr>
                <td colSpan={9} className="px-4 py-6 text-center text-slate-400">
                  Loading…
                </td>
              </tr>
            )}
            {!loading && manualScrapers.length === 0 && (
              <tr>
                <td colSpan={9} className="px-4 py-6 text-center text-slate-400">
                  No manual scrapers yet.
                </td>
              </tr>
            )}
            {manualScrapers.map((s) => (
              <tr key={s.id}>
                <td className="px-4 py-3 font-mono text-xs">{s.email}</td>
                <td className="px-4 py-3">{s.name || "—"}</td>
                <td className="px-4 py-3">
                  <span
                    className={
                      s.status === "active"
                        ? "rounded-full bg-emerald-50 px-2 py-0.5 text-xs font-medium text-emerald-700"
                        : s.status === "paused"
                          ? "rounded-full bg-amber-50 px-2 py-0.5 text-xs font-medium text-amber-700"
                          : "rounded-full bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-600"
                    }
                  >
                    {s.status}
                  </span>
                </td>
                <td className="px-4 py-3 text-xs text-slate-500">
                  {s.lastActiveAt
                    ? new Date(s.lastActiveAt).toLocaleString()
                    : "never"}
                </td>
                <td className="px-4 py-3 text-right tabular-nums">
                  {s.byStatus.pending ?? 0}
                </td>
                <td className="px-4 py-3 text-right tabular-nums">
                  {s.byStatus.claimed ?? 0}
                </td>
                <td className="px-4 py-3 text-right tabular-nums">
                  {s.byStatus.submitted ?? 0}
                </td>
                <td className="px-4 py-3 text-right tabular-nums">
                  {s.byStatus.qa_passed ?? 0}
                </td>
                <td className="px-4 py-3 text-right">
                  <div className="flex justify-end gap-3">
                    {s.status === "active" && (
                      <button
                        type="button"
                        onClick={() => resendInvite(s)}
                        disabled={resending === s.id}
                        className="text-xs text-slate-700 hover:text-slate-900 disabled:opacity-50"
                      >
                        {resending === s.id ? "Sending…" : "Resend invite"}
                      </button>
                    )}
                    {s.status === "active" ? (
                      <button
                        type="button"
                        onClick={() => setStatus(s.id, "paused")}
                        className="text-xs text-slate-500 hover:text-slate-900"
                      >
                        Pause
                      </button>
                    ) : (
                      <button
                        type="button"
                        onClick={() => setStatus(s.id, "active")}
                        className="text-xs text-emerald-600 hover:text-emerald-800"
                      >
                        Reactivate
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>
    </AdminLayout>
  );
}

export default function AdminManualScrapers() {
  return (
    <AdminGate>
      <AdminManualScrapersInner />
    </AdminGate>
  );
}
